<template>
  <Modal
    ref="modal"
    form
    full-height
    :loading="loading"
    :modal-style="{ maxWidth: '940px' }"
    offset-menu
    :title="modalTitle"
    @before-close="openCancelConfirm"
    @submit="submit"
  >
    <div
      v-if="!loading"
      class="q-py-md q-px-xl"
    >
      <template v-if="isEditing">
        <div v-if="showWarning">
          <InstantDownloadWarning
            :can-request-media-item="
              type === 'media_item' && canRequestMediaItem
            "
            :can-request-transcript="
              type === 'transcript_request' && canRequestTranscript
            "
            class="q-mb-lg"
            @media-item-button-clicked="openMediaItemRequest"
            @transcript-button-clicked="openTranscriptRequest"
          >
            <template #warningText>
              <div
                v-if="canRequest"
                class="row"
              >
                {{
                  $t(
                    `modals.create_instant_download_modal.${type}_warning_text`
                  )
                }}
              </div>
              <p v-else>
                <i18n-t
                  keypath="modals.create_instant_download_modal.warning_contact_text"
                >
                  <template #emailAddress>
                    <a
                      class="link"
                      href="mailto:account-managers@streem.com.au"
                      >account-managers@streem.com.au</a
                    >
                  </template>
                </i18n-t>
              </p>
            </template>
          </InstantDownloadWarning>
        </div>
        <FormStep :step="1">
          <div class="column full-width q-gutter-y-md q-pb-md">
            <div class="form-step-title">
              {{ syndicationLabel }}
            </div>
            <div
              v-if="mentionSyndications.length"
              class="row"
            >
              <span
                v-if="isCopiedStation"
                class="row q-pa-md q-my-sm bg-body content-border-radius full-width"
              >
                <div class="column q-pr-sm">
                  <QIcon name="info" />
                </div>
                <div class="col">
                  {{
                    $t(
                      `modals.create_instant_download_modal.${type}_download_text`,
                      {
                        syndicationSourceName: syndication.source_name,
                      }
                    )
                  }}
                </div>
              </span>
              <span
                v-else
                class="step-description"
              >
                {{
                  $t(
                    `modals.create_instant_download_modal.${type}_${mentionTypeKey}_create_text`
                  )
                }}
              </span>
            </div>
            <div
              v-if="mentionSyndications.length"
              class="row"
            >
              <InputSelect
                v-model="syndication"
                :disabled="isCopiedStation"
                emit-value
                new-design
                option-label="label"
                option-prop="value"
                :options="mentionSyndications"
                @update:model-value="loadMedia"
              />
            </div>
            <div
              v-if="!mentionSyndications.length"
              class="row justify-between"
            >
              <div class="col">
                <div class="field-heading q-mb-sm">
                  {{ mentionType }}
                </div>
                <div>
                  {{ mention.source.name }} {{ mention.source.location }}
                </div>
              </div>
            </div>
            <div class="row justify-between">
              <div class="col">
                <div class="field-heading">
                  {{
                    $t("modals.create_instant_download_modal.program_heading")
                  }}
                </div>
                <div
                  v-if="mention.program_airing"
                  class="row q-pt-xs"
                >
                  {{ mention.program_airing.name }}
                </div>
              </div>
              <div class="col">
                <div class="field-heading">
                  {{ $t("modals.create_instant_download_modal.time_heading") }}
                </div>
                <div class="row q-pt-xs">
                  {{
                    formatIntlDate(mentionStartTime, {
                      dateStyle: "short",
                      timeStyle: "medium",
                      timeZone: selectedMention.source.time_zone,
                    })
                  }}
                  {{ shortTimezone }}
                </div>
              </div>
              <div class="col">
                <div class="field-heading">
                  {{
                    $t(
                      "modals.create_instant_download_modal.mention_stream_heading"
                    )
                  }}
                </div>
                <div class="row q-pt-xs">
                  {{ stream.label }}
                </div>
              </div>
              <div class="col">
                <div class="field-heading">
                  {{
                    $t("modals.create_instant_download_modal.keyword_heading")
                  }}
                </div>
                <div class="row q-pt-xs">
                  {{ selectedMentionKeywords }}
                </div>
              </div>
            </div>
          </div>
        </FormStep>
        <FormStep :step="2">
          <div class="column full-width q-gutter-y-md q-pb-md">
            <div class="form-step-title">
              {{ editTitle }}
            </div>
            <div class="row">
              <span class="step-description">
                <i18n-t
                  :keypath="editTextKey"
                  scope="global"
                >
                  <template #newline>
                    <br />
                  </template>
                </i18n-t>
                <br />
                <strong>
                  {{ editDurationText }}
                </strong>
              </span>
            </div>
            <div class="row content-border-radius">
              <InlinePlayer
                v-if="currentClip"
                ref="player"
                allow-non-current-affairs-playback
                auto-fetch-neighbours
                :boundary-end-time="lastClipEndTime"
                :boundary-start-time="firstClipTime"
                :class="{ radio: !isTvRequest }"
                :clip="currentClip"
                hide-slider
                :poster="playerPoster"
                :start-time="playerCurrentTime"
                :timezone="timezone"
                :type="playerType"
                @update-time="updatePlayerCurrentTime"
              />
              <div
                v-if="clips.length"
                class="full-width bg-ds-denim-10 timeline-divider clipper-border-radius"
              >
                <ClipRangeTimeline
                  :clips="clips"
                  :range-end-time="toSeconds(instantDownloadRequest.endTime)"
                  :range-start-time="
                    toSeconds(instantDownloadRequest.startTime)
                  "
                  :timeline-current-time="playerCurrentTime"
                  :timezone="timezone"
                  @change-clip="updateClip"
                  @on-range-end-dropped="
                    setDateTimeProperty('endDateTime', $event)
                  "
                  @on-range-start-dropped="
                    setDateTimeProperty('startDateTime', $event)
                  "
                  @update-timeline-current-time="findClipAndUpdateCurrentTime"
                />
                <div
                  class="row q-py-md full-width timeline-divider justify-between"
                >
                  <div class="col q-pl-md">
                    <div class="row q-pb-sm text-white">
                      {{
                        $t(
                          "modals.create_instant_download_modal.start_time_label"
                        )
                      }}
                    </div>
                    <div class="row player-input title-font-size bg-white">
                      <div class="column full-width justify-center">
                        <InputTimePicker
                          v-model="startDateTime"
                          borderless
                          :max-time="endDateTime"
                          :min-time="boundarySelectableStartTime"
                          show-seconds
                          :timezone="timezone"
                        >
                          <template #prepend>
                            <QIcon
                              class="q-ml-sm"
                              name="far fa-clock"
                            />
                          </template>
                        </InputTimePicker>
                      </div>
                    </div>
                  </div>
                  <div class="col q-px-md">
                    <div class="row q-pb-sm text-white">
                      {{
                        $t(
                          "modals.create_instant_download_modal.end_time_label"
                        )
                      }}
                    </div>
                    <div class="row player-input title-font-size bg-white">
                      <div class="column full-width justify-center">
                        <InputTimePicker
                          v-model="endDateTime"
                          borderless
                          :max-time="boundarySelectableEndTime"
                          :min-time="startDateTime"
                          show-seconds
                          :timezone="timezone"
                        >
                          <template #prepend>
                            <QIcon
                              class="q-ml-sm"
                              name="far fa-clock"
                            />
                          </template>
                        </InputTimePicker>
                      </div>
                    </div>
                  </div>
                  <div class="col q-pr-md">
                    <div class="row q-pb-sm text-white">
                      {{
                        $t(
                          "modals.create_instant_download_modal.duration_label"
                        )
                      }}
                    </div>
                    <div
                      class="row q-pa-md player-input justify-between bg-white"
                    >
                      <div
                        class="text-ds-denim-9 title-font-size"
                        :class="{ error: outOfBoundaries }"
                      >
                        {{ clipDuration }}
                      </div>
                      <div
                        class="duration-text text-faded-grey"
                        :class="{ error: isBeyondClipDuration }"
                      >
                        {{
                          $t(
                            "modals.create_instant_download_modal.max_minutes_label",
                            { durationInMinutes }
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="row q-pa-md bg-body content-border-radius"
              :class="{ 'error-background': isBeyondClipDuration }"
            >
              <div class="column q-pr-sm">
                <QIcon name="info" />
              </div>
              <div class="col">
                <i18n-t
                  v-if="isBeyondClipDuration"
                  :keypath="`modals.create_instant_download_modal.${type}_exceed_minutes_text`"
                  scope="global"
                  tag="span"
                >
                  <template #durationInMinutes>
                    {{ durationInMinutes }}
                  </template>
                  <template #request>
                    <i18n-t
                      v-if="canRequestMediaItem"
                      keypath="modals.create_instant_download_modal.exceed_minutes_request_text"
                      scope="global"
                      tag="span"
                    >
                      <template #request>
                        <span
                          v-if="type === 'media_item'"
                          class="cursor-pointer underline"
                          @click="openMediaItemRequest"
                        >
                          <strong>
                            {{
                              $t(
                                "modals.create_instant_download_modal.media_item_request_text"
                              )
                            }}
                          </strong>
                        </span>
                        <span
                          v-if="type === 'transcript_request'"
                          class="cursor-pointer underline"
                          @click="openTranscriptRequest"
                        >
                          <strong>
                            {{
                              $t(
                                "modals.create_instant_download_modal.transcript_request_request_text"
                              )
                            }}
                          </strong>
                        </span>
                      </template>
                    </i18n-t>
                  </template>
                </i18n-t>
                <span v-else>
                  {{
                    $t(
                      `modals.create_instant_download_modal.${type}_max_minutes_text`,
                      {
                        durationInMinutes,
                      }
                    )
                  }}
                </span>
              </div>
            </div>
          </div>
        </FormStep>
        <FormStep :step="3">
          <div class="column full-width q-gutter-y-md q-pb-md">
            <div class="form-step-title">
              {{
                $t("modals.create_instant_download_modal.title_summary_title")
              }}
            </div>

            <InputText
              v-model="instantDownloadRequest.title"
              class="title-width"
              :label="$t('modals.create_instant_download_modal.title_label')"
              new-design
              :placeholder="
                $t('modals.create_instant_download_modal.title_placeholder')
              "
              :validations="['notBlank']"
            />

            <InputText
              v-model="instantDownloadRequest.summary"
              class="summary-width"
              height="90"
              :label="$t('modals.create_instant_download_modal.summary_label')"
              new-design
              :placeholder="
                $t('modals.create_instant_download_modal.summary_placeholder')
              "
              type="textarea"
              :validations="['notBlank']"
            />
          </div>
        </FormStep>
        <FormStep
          :is-last-step="!showFifthStep"
          :step="4"
        >
          <div class="column full-width q-gutter-y-md q-pb-md">
            <div class="form-step-title">
              {{ recipientAndThumbnailLabel }}
            </div>
            <div
              v-if="isTvRequest && thumbnails.length"
              class="row full-width overflow-hidden"
            >
              <div class="field-heading q-mb-sm">
                {{
                  $t("modals.create_instant_download_modal.thumbnail_heading")
                }}
              </div>
              <div
                class="row form-input-border full-width q-pa-sm scroll-x bg-color-white justify-center"
              >
                <div class="thumbnail-container full-width">
                  <img
                    v-for="thumbnail in thumbnails"
                    :key="thumbnail.id"
                    class="thumbnail q-mr-sm inline-block cursor-pointer"
                    :class="{
                      selected:
                        instantDownloadRequest.thumbnailUrl === thumbnail.url,
                    }"
                    :src="thumbnail.url"
                    @click="updateThumbnailUrl(thumbnail.url)"
                  />
                </div>
              </div>
            </div>
            <div>
              <div class="field-heading q-mb-sm">
                {{
                  $t("modals.create_instant_download_modal.recipients_heading")
                }}
              </div>
              <DistributedEmailInput
                v-model="instantDownloadRequest.recipients"
                new-design
              />
            </div>
          </div>
        </FormStep>
        <FormStep
          v-if="showFifthStep"
          is-last-step
          :step="5"
        >
          <div class="column full-width q-gutter-y-md q-pb-md">
            <template v-if="isTranscriptRequest">
              <div class="form-step-title">
                {{
                  $t(
                    "modals.create_instant_download_modal.transcript_request_format_title"
                  )
                }}
              </div>
              <div class="q-pt-sm row">
                {{
                  $t(
                    "modals.create_instant_download_modal.transcript_request_format_text"
                  )
                }}
              </div>
              <div class="row q-pa-md bg-body content-border-radius">
                <div class="column q-pr-sm">
                  <QIcon name="info" />
                </div>
                <div class="col">
                  <i18n-t
                    keypath="modals.create_instant_download_modal.transcript_request_format_description"
                    scope="global"
                    tag="span"
                  >
                    <template #platformName>
                      {{ platformName }}
                    </template>
                    <template
                      v-if="canRequestTranscript"
                      #request
                    >
                      <i18n-t
                        keypath="modals.create_instant_download_modal.transcript_request_format_description_request"
                        scope="global"
                        tag="span"
                      >
                        <template #request>
                          <a
                            class="link"
                            href="#"
                            @click.prevent="openTranscriptRequest"
                          >
                            {{
                              $t(
                                "modals.create_instant_download_modal.transcript_request_request_text"
                              )
                            }}</a
                          >
                        </template>
                      </i18n-t>
                    </template>
                  </i18n-t>
                </div>
              </div>
            </template>
            <template v-else-if="canSelectFormatsToInclude">
              <div class="form-step-title">
                {{
                  $t(
                    "modals.create_instant_download_modal.media_item_format_title"
                  )
                }}
              </div>

              <div class="format">
                <Radio
                  v-model="formatsToInclude"
                  class="format-input"
                  new-design
                  value="media_item"
                >
                  <div class="format-label">
                    <div>{{ selectMediaItemFormatLabel }}</div>
                    <div>
                      {{
                        $t(
                          "modals.create_instant_download_modal.media_item_select_description"
                        )
                      }}
                    </div>
                  </div>
                </Radio>

                <Radio
                  v-model="formatsToInclude"
                  class="format-input"
                  :disabled="noMoreTranscriptRequestCredit"
                  new-design
                  value="both"
                >
                  <div class="format-label">
                    <div>
                      {{ selectBothFormatsLabel }}
                    </div>
                    <div
                      v-if="noMoreTranscriptRequestCredit"
                      class="row q-pa-md bg-body content-border-radius"
                    >
                      <div class="column q-pr-sm">
                        <QIcon name="info" />
                      </div>
                      <div class="col">
                        <span>
                          {{
                            $t(
                              "modals.create_instant_download_modal.media_item_select_and_transcript_credits_description"
                            )
                          }}
                        </span>
                      </div>
                    </div>
                    <div v-else>
                      {{
                        $t(
                          "modals.create_instant_download_modal.media_item_select_description"
                        )
                      }}
                    </div>
                  </div>
                </Radio>
              </div>

              <div
                v-if="formatsToInclude === 'both'"
                class="row q-pa-md bg-body content-border-radius"
              >
                <div class="column q-pr-sm">
                  <QIcon name="info" />
                </div>
                <div class="col">
                  <i18n-t
                    keypath="modals.create_instant_download_modal.media_item_select_and_transcript_credits_text"
                    scope="global"
                    tag="span"
                  >
                    <template #platformName>
                      {{ appConfig.platformName }}
                    </template>
                    <template
                      v-if="canRequestTranscript"
                      #request
                    >
                      <i18n-t
                        keypath="modals.create_instant_download_modal.transcript_request_format_description_request"
                        scope="global"
                        tag="span"
                      >
                        <template #request>
                          <a
                            class="link"
                            href="#"
                            @click.prevent="openTranscriptRequest"
                          >
                            {{
                              $t(
                                "modals.create_instant_download_modal.transcript_request_request_text"
                              )
                            }}</a
                          >
                        </template>
                      </i18n-t>
                    </template>
                  </i18n-t>
                </div>
              </div>
            </template>
          </div>
        </FormStep>
        <div
          class="row q-ml-sm"
          :class="{ error: submitted && !acceptConditions }"
        >
          <div class="column full-width items-center">
            <Checkbox
              v-model="acceptConditions"
              new-design
            >
              <div class="column q-pl-md text-help-text">
                {{
                  $t(
                    `modals.create_instant_download_modal.${type}_toggle_accept_conditions_label`,
                    {
                      platformName: appConfig.platformName,
                      organisationName: organisation.name,
                    }
                  )
                }}
              </div>
            </Checkbox>
          </div>
        </div>
      </template>
      <template v-else>
        <InstantDownloadRequestPreview
          :first-clip="firstClip"
          :formats-to-include="formatsToInclude"
          :instant-download-request="instantDownloadRequest"
          :keywords="selectedMentionKeywords"
          :program-airing="selectedMentionProgramAiring"
          :source="selectedMentionSource"
          :stream-label="stream.label"
          :type="type"
          @preview-back-clicked="returnToEditing"
          @preview-process-clicked="processInstantDownloadRequest"
        />
      </template>
    </div>

    <template
      v-if="!loading"
      #buttons
    >
      <template v-if="isEditing">
        <BaseButton
          :label="$t('global.cancel')"
          new-design
          @click="openCancelConfirm()"
        />
        <BaseButton
          color="primary"
          :disabled="outOfBoundaries"
          :label="$t('global.preview')"
          new-design
          type="submit"
        />
      </template>
      <PortalTarget name="create-instant-download-footer" />
    </template>
  </Modal>
</template>

<script>
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";
import { PortalTarget } from "portal-vue";

import {
  BaseButton,
  InputSelect,
  InputText,
  InputTimePicker,
} from "shared/components/base";
import DistributedEmailInput from "shared/components/core/forms/DistributedEmailInput.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import FormStep from "shared/components/FormStep.vue";
import InstantDownloadRequestPreview from "shared/components/InstantDownloadRequestPreview.vue";
import InstantDownloadWarning from "shared/components/InstantDownloadWarning.vue";
import ClipRangeTimeline from "shared/components/players/ClipRangeTimeline.vue";
import InlinePlayer from "shared/components/players/InlinePlayer.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import {
  addTime,
  dateToTimestamp,
  durationTimer,
  formatIntlDate,
  parseDate,
  shortTimezone,
  subtractTime,
  toMilliseconds,
  toSeconds,
} from "shared/helpers/date";
import { lowerCase } from "shared/helpers/string";
import { MediaRequest, TranscriptRequest } from "shared/resources";
import { disableShortcuts, enableShortcuts } from "shared/services/shortcuts";
import { useGlobalStore } from "shared/stores/global";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";
import { useUserStore } from "shared/stores/user";

const DURATION_IN_MINUTES = 20;
const MAX_DURATION_IN_SECONDS = 60 * DURATION_IN_MINUTES;
const DEFAULT_CLIPS_GAP_THRESHOLD = 15;

export default {
  name: "CreateInstantDownloadModal",
  components: {
    PortalTarget,
    BaseButton,
    InputTimePicker,
    InputText,
    DistributedEmailInput,
    InstantDownloadRequestPreview,
    ClipRangeTimeline,
    InlinePlayer,
    FormStep,
    InputSelect,
    InstantDownloadWarning,
    Modal,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    stream: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: "media_item",
    },
    origin: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup() {
    const { refreshUsedCredits } = useOrganisationFeaturesStore();

    const userStore = useUserStore();
    const { currentUser, organisation } = storeToRefs(userStore);

    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    const { selectedTeamPreference } = useTeamPicker();

    return {
      refreshUsedCredits,
      currentUser,
      organisation,
      appConfig,
      selectedTeamPreference,
    };
  },
  data() {
    return {
      cancelEditingWarningDialog: false,
      isEditing: true,
      mediaRequest: new MediaRequest({
        startTime: toMilliseconds(this.mention.start_time),
        endTime: toMilliseconds(this.mention.end_time),
        recipients: [],
        instantDownload: true,
        notifyOnCompletion: true,
        billable: true,
      }),
      transcriptRequest: new TranscriptRequest({
        ...this.origin,
        startTime: toMilliseconds(this.mention.start_time),
        endTime: toMilliseconds(this.mention.end_time),
        recipients: [],
        instantDownload: true,
        notifyOnCompletion: true,
        formats: ["docx"],
        fileUrl: [],
        billable: true,
      }),
      syndication: null,
      recipients: [],
      acceptConditions: false,
      clips: [],
      currentClip: null,
      startClip: null,
      thumbnails: [],
      loading: false,
      playerCurrentTime: 0,
      submitted: false,
      debouncedFetchThumbnails: debounce(this.canFetchThumbnails, 500),
      formatsToInclude: "media_item",
      noMoreTranscriptRequestCredit: true,
      isCopiedStation: false,
      durationInMinutes: DURATION_IN_MINUTES.toString(),
    };
  },
  computed: {
    isMediaItem() {
      return this.type === "media_item";
    },
    modalTitle() {
      if (this.isEditing) {
        if (this.isMediaItem) {
          return this.$t(
            "modals.create_instant_download_modal.media_item_modal_title"
          );
        }

        return this.$t(
          "modals.create_instant_download_modal.transcript_request_modal_title"
        );
      }

      if (this.isMediaItem) {
        if (this.isTvRequest) {
          return this.$t(
            "modals.create_instant_download_modal.media_item_modal_tv_title"
          );
        }

        return this.$t(
          "modals.create_instant_download_modal.media_item_modal_radio_title"
        );
      }

      if (this.isTvRequest) {
        return this.$t(
          "modals.create_instant_download_modal.transcript_request_channel_modal_title"
        );
      }

      return this.$t(
        "modals.create_instant_download_modal.transcript_request_station_modal_title"
      );
    },
    editTitle() {
      if (this.isMediaItem) {
        return this.$t(
          "modals.create_instant_download_modal.media_item_edit_title"
        );
      }

      return this.$t(
        "modals.create_instant_download_modal.transcript_request_edit_title"
      );
    },
    editTextKey() {
      if (this.isMediaItem) {
        return "modals.create_instant_download_modal.media_item_edit_text";
      }

      return "modals.create_instant_download_modal.transcript_request_edit_text";
    },
    editDurationText() {
      if (this.isMediaItem) {
        return this.$t(
          "modals.create_instant_download_modal.media_item_max_minutes_text",
          { durationInMinutes: this.durationInMinutes }
        );
      }

      return this.$t(
        "modals.create_instant_download_modal.transcript_request_max_minutes_text",
        { durationInMinutes: this.durationInMinutes }
      );
    },
    canRequest() {
      return this.isMediaItem
        ? this.canRequestMediaItem
        : this.canRequestTranscript;
    },
    canRequestMediaItem() {
      return (
        this.$features.has("media_request") &&
        this.$permissions.has("can_request_media_items")
      );
    },
    canRequestTranscript() {
      return (
        this.$features.has("audio_transcript") &&
        this.$permissions.has("can_request_transcripts")
      );
    },
    canInstantEditTranscriptRequest() {
      return (
        this.$features.has("has_instant_edit_transcript") &&
        this.$permissions.has("can_create_instant_edit_transcripts")
      );
    },
    canInstantEditMediaItem() {
      return (
        this.$features.has("has_instant_edit_media_item") &&
        this.$permissions.has("can_create_instant_edit_media_items")
      );
    },
    playerPoster() {
      if (this.isTvRequest) return "none";

      return this.mention.source.logo_url;
    },
    playerType() {
      return this.isTvRequest ? "video" : "audio";
    },
    isTvRequest() {
      return this.mentionType === "Channel";
    },
    sourceType() {
      return this.isTvRequest ? "TvChannel" : "RadioStation";
    },
    timezone() {
      return this.selectedMention.source.time_zone;
    },
    shortTimezone() {
      return shortTimezone(
        toMilliseconds(this.selectedMention.start_time),
        this.timezone
      );
    },
    isBeyondClipDuration() {
      const diff = this.findMediaRequestDuration();

      return (
        this.instantDownloadRequest.startTime &&
        this.instantDownloadRequest.endTime &&
        diff >= MAX_DURATION_IN_SECONDS
      );
    },
    outOfBoundaries() {
      const diff = this.findMediaRequestDuration();

      return (
        this.instantDownloadRequest.startTime &&
        this.instantDownloadRequest.endTime &&
        (diff <= 0 || diff >= MAX_DURATION_IN_SECONDS)
      );
    },
    clipDuration() {
      return durationTimer(
        new Date(this.instantDownloadRequest.endTime),
        new Date(this.instantDownloadRequest.startTime)
      );
    },
    mentionTypeKey() {
      return this.mention.type === "radio_clip" ? "station" : "channel";
    },
    mentionType() {
      return this.mention.type === "radio_clip"
        ? this.$t("modals.create_instant_download_modal.station_mention_type")
        : this.$t("modals.create_instant_download_modal.channel_mention_type");
    },
    mentionSyndications() {
      if (this.mention.syndications && this.mention.syndications.length) {
        const syndications = this.mention.syndications.map((syndication) => ({
          label: syndication.source_name,
          value: syndication,
        }));

        syndications.unshift({
          label: `${this.mention.source.name} ${this.mention.source.location}`,
          value: this.mention,
        });

        return syndications;
      }

      return [];
    },
    syndicationsArePresent() {
      return this.syndication && Object.keys(this.syndication).length;
    },
    mentionStartTime() {
      return this.syndicationsArePresent
        ? this.syndication.start_time
        : this.mention.start_time;
    },
    selectedMention() {
      return this.syndicationsArePresent ? this.syndication : this.mention;
    },
    selectedMentionSource() {
      return this.selectedMention?.source;
    },
    selectedMentionProgramAiring() {
      return this.selectedMention.program_airing;
    },
    selectedMentionKeywords() {
      const keywords = this.selectedMention.keywords || [];

      return Object.keys(keywords).join(", ");
    },
    hasGaps() {
      if (!this.clips.length) return false;

      for (let index = 1; index < this.clips.length; index += 1) {
        const diff =
          this.clips[index].start_time - this.clips[index - 1].end_time;

        if (diff > this.sourceGapThreshold) return true;
      }

      return false;
    },
    sourceGapThreshold() {
      return (
        this.mention.source?.clips_gap_threshold_seconds ||
        DEFAULT_CLIPS_GAP_THRESHOLD
      );
    },
    firstClipTime() {
      const clipsLength = this.clips?.length;

      return clipsLength ? this.clips[0].start_time : null;
    },
    lastClipEndTime() {
      const clipsLength = this.clips?.length;

      return clipsLength ? this.clips[clipsLength - 1].start_time : null;
    },
    recipientAndThumbnailLabel() {
      return this.isTvRequest && this.thumbnails?.length
        ? this.$t(
            "modals.create_instant_download_modal.thumbnail_recipients_tv_label"
          )
        : this.$t("modals.create_instant_download_modal.add_receipients_label");
    },
    syndicationLabel() {
      if (this.mentionSyndications?.length) {
        if (this.isTvRequest) {
          return this.$t(
            "modals.create_instant_download_modal.syndication_select_channel_title"
          );
        }

        return this.$t(
          "modals.create_instant_download_modal.syndication_select_station_title"
        );
      }

      if (this.isTvRequest) {
        return this.$t(
          "modals.create_instant_download_modal.syndication_channel_title"
        );
      }

      return this.$t(
        "modals.create_instant_download_modal.syndication_station_title"
      );
    },
    startDateTime: {
      get() {
        return new Date(this.instantDownloadRequest.startTime);
      },
      set(newValue) {
        const startTimestamp = Number(newValue);
        this.instantDownloadRequest.startTime = startTimestamp;
        this.firstClip = this.findClipInRange(startTimestamp);
      },
    },
    endDateTime: {
      get() {
        return new Date(this.instantDownloadRequest.endTime);
      },
      set(newValue) {
        const endTimestamp = Number(newValue);
        this.instantDownloadRequest.endTime = endTimestamp;
      },
    },
    startTimeSelectableRange() {
      const mentionEndTimeInSeconds = this.endDateTime;

      return [this.boundarySelectableStartTime, mentionEndTimeInSeconds];
    },
    endTimeSelectableRange() {
      const startTimeInSeconds = this.startDateTime;

      return [startTimeInSeconds, this.boundarySelectableEndTime];
    },
    boundarySelectableStartTime() {
      const firstClipDate = parseDate(this.firstClipTime);

      const twentyMinuteTimeFromEndDateTime = subtractTime(
        this.endDateTime,
        DURATION_IN_MINUTES,
        "minute"
      );

      return twentyMinuteTimeFromEndDateTime > firstClipDate
        ? twentyMinuteTimeFromEndDateTime
        : firstClipDate;
    },
    boundarySelectableEndTime() {
      const lastClipDate = parseDate(this.lastClipEndTime);

      const twentyMinuteTimeFromStartDateTime = addTime(
        this.startDateTime,
        DURATION_IN_MINUTES,
        "minute"
      );

      return twentyMinuteTimeFromStartDateTime < lastClipDate
        ? twentyMinuteTimeFromStartDateTime
        : lastClipDate;
    },
    afterStartDate() {
      const twentyMinsBeforeMention =
        this.selectedMention.start_time - DURATION_IN_MINUTES * 60;

      return (
        twentyMinsBeforeMention -
        toSeconds(this.getExcessTimeOverNow(this.beforeStartDate))
      );
    },
    beforeStartDate() {
      return this.selectedMention.start_time + DURATION_IN_MINUTES * 60;
    },
    isTranscriptRequest() {
      return this.type === "transcript_request";
    },
    instantDownloadRequest() {
      return this.type === "media_item"
        ? this.mediaRequest
        : this.transcriptRequest;
    },
    itemType() {
      return this.type === "media_item"
        ? this.$t("modals.create_instant_download_modal.media_item_type")
        : this.$t(
            "modals.create_instant_download_modal.transcript_request_type"
          );
    },
    showFifthStep() {
      return this.isTranscriptRequest || this.canSelectFormatsToInclude;
    },
    canSelectFormatsToInclude() {
      return this.type === "media_item" && this.canInstantEditTranscriptRequest;
    },
    selectMediaItemFormatLabel() {
      return this.isTvRequest
        ? this.$t(
            "modals.create_instant_download_modal.media_item_select_video_option"
          )
        : this.$t(
            "modals.create_instant_download_modal.media_item_select_audio_option"
          );
    },
    selectBothFormatsLabel() {
      return this.isTvRequest
        ? this.$t(
            "modals.create_instant_download_modal.media_item_select_and_transcript_video_option"
          )
        : this.$t(
            "modals.create_instant_download_modal.media_item_select_and_transcript_audio_option"
          );
    },
    showWarning() {
      return this.hasGaps || !this.clips.length;
    },
    isCopiedFromStation() {
      return (
        this.mention.type === "radio_clip" &&
        this.mention.original &&
        this.mention.original.station_id
      );
    },
  },
  watch: {
    startDateTime: {
      immediate: true,
      handler: "debouncedFetchThumbnails",
    },
    endDateTime: {
      immediate: true,
      handler: "debouncedFetchThumbnails",
    },
  },
  async mounted() {
    disableShortcuts();

    this.loadSyndication();

    this.instantDownloadRequest.sourceType = this.sourceType;
    this.instantDownloadRequest.sourceId = this.mention.source.id;

    this.loadMedia();

    this.playerCurrentTime = toMilliseconds(this.selectedMention.start_time);

    await this.refreshCredits();
  },
  beforeUnmount() {
    enableShortcuts();
  },
  methods: {
    formatIntlDate,
    lowerCase,
    toSeconds,
    findMediaRequestDuration() {
      return Math.floor(
        toSeconds(
          this.instantDownloadRequest.endTime -
            this.instantDownloadRequest.startTime
        )
      );
    },
    updateClip(clip) {
      this.currentClip = clip;
    },
    updatePlayerCurrentTime(time) {
      this.playerCurrentTime = time;
    },
    canFetchThumbnails() {
      if (this.isTvRequest) {
        this.fetchThumbnails();
      }
    },
    async fetchThumbnails() {
      try {
        const response = await this.$streemApiV1.get(
          `tv_channels/${this.selectedMention.source.id}/thumbnails`,
          {
            params: {
              after: dateToTimestamp(this.startDateTime),
              before: dateToTimestamp(this.endDateTime),
              order: "asc",
            },
          }
        );

        this.thumbnails = response.data;
        this.preSelectFirstThumbnailUrl(this.thumbnails);
      } catch {
        // If we're unable to fetch thumbnails we simply don't show them
      }
    },
    loadMedia() {
      this.$track("Instant Edit Download syndication clips changed");
      this.loadClips();
      this.canFetchThumbnails();
    },
    async loadClips() {
      try {
        this.loading = true;

        const endPoint = this.isTvRequest ? "tv_channels" : "radio_stations";

        const response = await this.$streemApiV1.get(
          `${endPoint}/${this.selectedMention.source.id}/clips`,
          {
            params: {
              after: this.afterStartDate,
              before: this.beforeStartDate,
              limit: 80,
              include_program_airing: true,
            },
          }
        );

        this.clips = response.data;
        const mentionStartTime = this.selectedMention.start_time;

        this.instantDownloadRequest.startTime = toMilliseconds(
          this.selectedMention.start_time
        );

        this.instantDownloadRequest.endTime = toMilliseconds(
          this.selectedMention.end_time
        );

        this.currentClip = this.clips.find(
          (clip) =>
            clip.start_time <= mentionStartTime &&
            clip.end_time >= mentionStartTime
        );

        this.firstClip = this.currentClip;

        this.playerCurrentTime = toMilliseconds(
          this.selectedMention.start_time
        );
      } catch {
        this.$alert(
          this.$t("modals.create_instant_download_modal.confirm_modal_error")
        );
      } finally {
        this.loading = false;
      }
    },
    submit() {
      this.submitted = true;

      if (this.acceptConditions) {
        this.isEditing = false;
        this.$track("Instant Edit Download transitioning to preview");
      }
    },
    openCancelConfirm(cancel = () => {}) {
      if (this.hasGaps) {
        this.closeDialog();
      } else {
        cancel();

        const title = this.isMediaItem
          ? this.$t(
              "modals.create_instant_download_modal.media_item_confirm_cancel_title"
            )
          : this.$t(
              "modals.create_instant_download_modal.transcript_request_confirm_cancel_title"
            );

        const message = this.isMediaItem
          ? this.$t(
              "modals.create_instant_download_modal.media_item_confirm_cancel_message"
            )
          : this.$t(
              "modals.create_instant_download_modal.transcript_request_confirm_cancel_message"
            );

        this.$dialog
          .confirm({
            theme: "danger",
            title,
            message,
          })
          .onOk(() => {
            this.closeDialog();
          });
      }
    },
    getExcessTimeOverNow(timestamp) {
      const value = toMilliseconds(timestamp);
      const nowInMilliseconds = Date.now();

      if (value > nowInMilliseconds) {
        return value - nowInMilliseconds;
      }

      return 0;
    },
    returnToEditing() {
      this.isEditing = true;
    },
    async processInstantDownloadRequest() {
      try {
        this.$q.loading.show({ delay: 0 });

        const requestToSave =
          this.instantDownloadRequest.dupWithDirtyTracking();

        if (this.type === "transcript_request") {
          requestToSave.startTime = toSeconds(requestToSave.startTime);
          requestToSave.endTime = toSeconds(requestToSave.endTime);
          requestToSave.description = requestToSave.summary;
        } else {
          requestToSave.sourceType = this.sourceType;
          requestToSave.sourceId = this.selectedMention.source.id;
        }

        if (this.$features.has("has_team_workspaces")) {
          requestToSave.organisationTeamId = this.selectedTeamPreference;
        }

        await requestToSave.saveResource();

        this.$track("Instant Edit Download clip requested", {
          instantDownloadRequestId: requestToSave.id,
          sourceType: requestToSave.sourceType,
          sourceId: requestToSave.sourceId,
          streamId: this.stream.id,
          mentionId: this.mention.id,
          type: this.type,
        });

        if (this.formatsToInclude === "both") {
          // Also create the Transcript Request
          this.transcriptRequest.title = requestToSave.title;
          this.transcriptRequest.sourceType = this.sourceType;
          this.transcriptRequest.sourceId = this.selectedMention.source.id;
          this.transcriptRequest.startTime = toSeconds(requestToSave.startTime);
          this.transcriptRequest.endTime = toSeconds(requestToSave.endTime);
          this.transcriptRequest.description = requestToSave.summary;

          if (this.$features.has("has_team_workspaces")) {
            this.transcriptRequest.organisationTeamId =
              this.selectedTeamPreference;
          }

          await this.transcriptRequest.saveResource();

          this.$track("Instant-edit Transcript also requested", {
            instantDownloadRequestId: this.transcriptRequest.id,
            sourceType: this.transcriptRequest.sourceType,
            sourceId: this.transcriptRequest.sourceId,
            streamId: this.stream.id,
            mentionId: this.mention.id,
            type: "transcript_request",
          });
        }

        this.closeDialog();

        this.$modals.open("CreateInstantDownloadConfirmModal", {
          props: {
            type: this.itemType,
          },
          events: {
            confirm: () => {
              this.$track(
                "Instant Edit Download clip confirmation acknowledged"
              );

              this.$modals.close("CreateInstantDownloadConfirmModal");
            },
          },
        });
      } catch {
        this.$alert(
          this.$t(
            "modals.create_instant_download_modal.unable_to_complete_request"
          )
        );
      } finally {
        this.$q.loading.hide();
      }
    },
    closeDialog() {
      this.$refs.modal.close();
    },
    getPlayerTimestamp() {
      return this.$refs.player.getCurrentTimestamp();
    },
    setDateTimeProperty(property, timestamp = null) {
      const playerTimestamp = timestamp || this.getPlayerTimestamp();
      this[property] = playerTimestamp;
    },
    updateThumbnailUrl(thumbnailUrl) {
      this.instantDownloadRequest.thumbnailUrl = thumbnailUrl;
    },
    openMediaItemRequest() {
      const trackMessage = this.outOfBoundaries
        ? "Instant Edit Download requested AM intervention"
        : "Instant Edit Download clips unavailable";

      this.$track(trackMessage, {
        userId: this.currentUser.id,
        sourceType: this.instantDownloadRequest.sourceType,
        sourceId: this.instantDownloadRequest.sourceId,
        streamId: this.stream.id,
        mentionId: this.mention.id,
      });

      this.instantDownloadRequest.sourceId = this.selectedMention.source.id;

      this.$refs.modal.close();

      this.$modals.open("RequestMediaItemModal", {
        props: {
          mention: this.mention,
          stream: this.stream,
          origin: this.stream,
          instantDownloadRequest: this.instantDownloadRequest,
        },
      });
    },
    openTranscriptRequest() {
      const trackMessage =
        "Instant Edit Download requested verbatim transcription";

      this.$track(trackMessage, {
        userId: this.currentUser.id,
        streamId: this.stream.id,
        mentionId: this.mention.id,
      });

      this.$refs.modal.close();

      this.$modals.open("RequestTranscriptItemModal", {
        props: {
          mention: this.mention,
          origin: this.stream,
        },
      });
    },
    findClipInRange(currentTime) {
      return this.clips.find(
        (clip) =>
          currentTime >= toMilliseconds(clip.start_time) &&
          currentTime <= toMilliseconds(clip.end_time)
      );
    },
    findClipAndUpdateCurrentTime(currentTime) {
      const foundClip = this.findClipInRange(currentTime);

      if (foundClip) {
        this.updateClip(foundClip);
      }

      this.updatePlayerCurrentTime(currentTime);
      this.$nextTick(this.$refs.player.updatePlayerStart);
    },
    preSelectFirstThumbnailUrl(thumbnails) {
      const preSelectedThumbnailUrl =
        thumbnails?.length && this.isTvRequest ? thumbnails[0].url : null;

      this.updateThumbnailUrl(preSelectedThumbnailUrl);
    },
    async refreshCredits() {
      if (this.canInstantEditTranscriptRequest) {
        await this.refreshUsedCredits({
          organisationId: this.organisation.id,
          feature: "has_instant_edit_transcript",
        });

        this.noMoreTranscriptRequestCredit =
          this.$features.reachedContractAllowance(
            "has_instant_edit_transcript"
          );
      }

      if (this.canInstantEditMediaItem && this.type === "media_item") {
        await this.refreshUsedCredits({
          organisationId: this.organisation.id,
          feature: "has_instant_edit_media_item",
        });
      }
    },
    loadSyndication() {
      if (this.isCopiedFromStation) {
        const originalSyndication = this.mention.syndications.find(
          (syndication) =>
            syndication.station_id === this.mention.original.station_id
        );

        this.syndication = originalSyndication;
        this.isCopiedStation = true;
      } else {
        this.syndication = this.mentionSyndications.length
          ? this.mention
          : null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.thumbnail-container {
  white-space: nowrap;

  .thumbnail {
    width: 140px;

    &.selected {
      border: 4px solid $online;
    }
  }
}

.error {
  color: $error;

  :deep(.el-input__inner) {
    color: $error;
  }
}

.timeline-divider {
  border-top: 1px solid var(--s-color-denim-7);
}

:deep(.timer) {
  color: $white;
  text-align: left;
}

:deep(.button-bar) {
  &.tv,
  &.radio {
    background-color: var(--s-color-denim-10);
  }

  button {
    color: $white;
  }
}

:deep(.radio) {
  video[poster] {
    height: 320px;
  }
}

.primary-text-color {
  color: $black;
}

.error-background {
  background: rgb(230 91 80 / 10%);
}

.player-input {
  height: 50px;
  border-radius: 3px;
}

.step-description {
  font-size: 12px;
  line-height: 150%;
}

.form-step-title {
  font-size: 16px;
  height: 30px;
  display: flex;
  align-items: center;
}

.duration-text {
  font-size: 12px;
  padding-bottom: 4px;
  padding-top: 4px;
}

.title-width {
  width: 40%;
}

.summary-width {
  width: 75%;
}

.clipper-border-radius {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.content-border-radius {
  border-radius: 4px;
}

:deep(.el-input__inner) {
  background-color: $white;
  color: var(--s-color-denim-9);
  padding-right: 10px;
}

:deep(video) {
  background-color: var(--s-color-denim-9);
}

.format {
  display: flex;
  flex-direction: column;
  gap: var(--s-spacing-sm);

  &-input {
    border: 1px solid var(--s-input-default-border);
    border-radius: var(--s-border-radius-md);
    padding-block: var(--s-spacing-md);
    padding-inline: var(--s-spacing-lg);
    margin: 0;
    gap: var(--s-spacing-lg);
  }

  &-label {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-md);
  }
}
</style>
